import { distanceUnitByUnitType } from '@racemap/utilities/functions/units';
import type { UnitType } from '@racemap/utilities/types/units';
import update from 'immutability-helper';
import { useEffect, useState } from 'react';
import type { MapStyle } from 'react-map-gl-legacy/dist/esm/types';
import {
  BASE_BLUE,
  MAPBOX_ACCESS_TOKEN,
  MAP_STYLES,
  SHADOW_TRACK_STEP,
} from 'src/lib/player/constants';
import type { ExternalLayerType, TrackType } from 'src/lib/player/store';
import {
  convertColorArrayToString,
  distanceUnitToMeterFactor,
  meterToDistanceUnitFactor,
  validateColor,
} from 'src/lib/player/utils';
import type TrackView from 'src/lib/player/utils/TrackView';

// From https://github.com/mapbox/mapbox-gl-language/issues/31#issuecomment-510307581
function fixLanguage(layers: Array<any>, language: string) {
  const labelList = layers.filter((layer) => {
    return /-label/.test(layer.id);
  });
  for (const labelLayer of labelList) {
    labelLayer.layout['text-field'] = ['coalesce', ['get', `name_${language}`], ['get', 'name']];
  }
}

function getMapStyleKey(mapType: string) {
  const mapStyleInfo = MAP_STYLES.find((info) => info.label === mapType);
  return mapStyleInfo != null ? mapStyleInfo.key : MAP_STYLES[0].key;
}

const mapStyleCache = new Map<string, MapStyle>();

async function loadMapStyle(mapStyleKey: string): Promise<MapStyle> {
  const res = await fetch(
    `https://api.mapbox.com/styles/v1/mapbox/${mapStyleKey}?optimize=true&access_token=${MAPBOX_ACCESS_TOKEN}`,
  );
  const mapStyleData = await res.json();
  fixLanguage(mapStyleData.layers, 'mul');
  return mapStyleData;
}

async function loadMapStyleMemoized(mapStyleKey: string): Promise<MapStyle> {
  const storedMapStyle = mapStyleCache.get(mapStyleKey);
  if (storedMapStyle != null) {
    return storedMapStyle;
  }

  const promise = await loadMapStyle(mapStyleKey);
  mapStyleCache.set(mapStyleKey, promise);
  return promise;
}

function drawExternalLayers(mapStyle: MapStyle, externalLayers: Array<ExternalLayerType>) {
  let newMapStyle = mapStyle;
  for (const externalLayer of externalLayers) {
    const featureId = externalLayer.id;
    const layer = {
      id: featureId,
      type: 'raster',
      source: featureId,
      paint: {},
    };
    const source = {
      type: 'raster',
      tiles: externalLayer.tiles,
      tileSize: externalLayer.tileSize,
    };
    newMapStyle = update(newMapStyle, {
      layers: { $push: [layer] },
      sources: {
        [featureId]: {
          $set: source,
        },
      },
    });
  }
  return newMapStyle;
}
function drawTracks(mapStyle: MapStyle, tracks: Array<TrackType>, selectedIds: Array<string>) {
  function drawTrack(track: TrackType, isSelected: boolean) {
    const featureId = track.id;
    const layer = {
      id: featureId,
      type: 'line',
      source: featureId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': validateColor(track.markerColor.tuple)
          ? convertColorArrayToString(track.markerColor.tuple)
          : BASE_BLUE,
        'line-width': isSelected ? 5 : 3,
        'line-opacity': 0.8,
      },
    };
    const source = {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: track.coordinates.map((ll) => [ll[0], ll[1]]),
        },
      },
    };
    newMapStyle = update(newMapStyle, {
      layers: { $push: [layer] },
      sources: {
        [featureId]: {
          $set: source,
        },
      },
    });
  }
  let newMapStyle = mapStyle;
  for (const track of tracks) {
    if (!selectedIds.includes(track.id)) {
      drawTrack(track, false);
    }
  }
  for (const track of tracks) {
    if (selectedIds.includes(track.id)) {
      drawTrack(track, true);
    }
  }
  return newMapStyle;
}

function drawDistanceMarkers(
  mapStyle: MapStyle,
  chunkedShadowTrack: TrackView,
  shadowTrackIndex: number,
  unitType: UnitType,
  largeMode: boolean,
) {
  const trackLength =
    chunkedShadowTrack.size() * SHADOW_TRACK_STEP * meterToDistanceUnitFactor(unitType);
  const strides = [
    { stride: 1, minzoom: 12.7, maxzoom: 24 },
    { stride: 2, minzoom: 12, maxzoom: 12.7 },
    { stride: 5, minzoom: 10.7, maxzoom: 12 },
    { stride: 10, minzoom: 10, maxzoom: 10.7 },
    { stride: 20, minzoom: 8.5, maxzoom: 10 },
    { stride: 50, minzoom: 7.5, maxzoom: 8.5 },
    { stride: 100, minzoom: 7, maxzoom: 7.5 },
    { stride: 200, minzoom: 6, maxzoom: 7 },
    { stride: 500, minzoom: 5, maxzoom: 6 },
  ].filter((s) => s.stride < trackLength);

  function makeFeatures(stride: { stride: number; minzoom: number; maxzoom: number }) {
    const out = [];
    const strideInSteps = (stride.stride * distanceUnitToMeterFactor(unitType)) / SHADOW_TRACK_STEP;
    const stepCount = chunkedShadowTrack.size() / strideInSteps;
    for (let i = 1; i < stepCount; i++) {
      out.push({
        type: 'Feature',
        properties: { unit: distanceUnitByUnitType(unitType), distance: i * stride.stride },
        geometry: {
          type: 'Point',
          coordinates: chunkedShadowTrack.getLngLat(Math.floor(i * strideInSteps)),
        },
      });
    }
    return out;
  }

  let newMapStyle = mapStyle;
  for (const stride of strides) {
    const featureId = `distance-marker-${shadowTrackIndex}-stride${stride.stride}`;
    const circleLayer = {
      id: `${featureId}-circle`,
      type: 'circle',
      source: featureId,
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': largeMode ? 14 : 9,
        'circle-stroke-color': '#000000',
        'circle-stroke-width': 1,
      },
      minzoom: stride.minzoom,
      maxzoom: stride.maxzoom,
    };
    const numLayer = {
      id: `${featureId}-num`,
      type: 'symbol',
      source: featureId,
      paint: {
        'text-color': '#000000',
      },
      layout: {
        'symbol-placement': 'point',
        'text-field': '{distance}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
        'text-anchor': 'center',
        'text-justify': 'center',
        'text-size': largeMode ? 16 : 9,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      minzoom: stride.minzoom,
      maxzoom: stride.maxzoom,
    };
    const trackSource = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: makeFeatures(stride),
      },
    };

    newMapStyle = update(newMapStyle, {
      layers: { $push: [circleLayer, numLayer] },
      sources: {
        [featureId]: {
          $set: trackSource,
        },
      },
    });
  }
  return newMapStyle;
}

export function useMapStyle(
  mapType: string,
  tracks: Array<TrackType>,
  externalLayers: Array<ExternalLayerType>,
  shadowTracks: Array<[TrackView, boolean]>,
  selectedIds: Set<string>,
  unitType: UnitType,
  largeMode: boolean,
) {
  const [mapStyle, setMapStyle] = useState<MapStyle | null>(null);

  const selectedTracksIdString = tracks
    .filter((track) => selectedIds.has(track.id))
    .map((track) => track.id)
    .sort()
    .join('::');

  useEffect(() => {
    (async () => {
      let mapStyleData = await loadMapStyleMemoized(getMapStyleKey(mapType));
      mapStyleData = drawExternalLayers(mapStyleData, externalLayers);
      mapStyleData = drawTracks(mapStyleData, tracks, selectedTracksIdString.split('::'));
      for (let i = 0; i < shadowTracks.length; i++) {
        const showDistanceMarkers = shadowTracks[i][1];
        if (showDistanceMarkers) {
          mapStyleData = drawDistanceMarkers(
            mapStyleData,
            shadowTracks[i][0],
            i,
            unitType,
            largeMode,
          );
        }
      }
      setMapStyle(mapStyleData);
    })();
  }, [mapType, tracks, externalLayers, shadowTracks, selectedTracksIdString, unitType, largeMode]);

  return mapStyle;
}
